/**
 * sessionStorage backup for in-flight IR edits during the create-monitor
 * wizard. The backend doesn't expose a PUT /draft/{id} (out of MVP
 * scope), so user edits in Step 2's IRForm live only in component state.
 * Without a backup, reloading the wizard at step 2 or step 3 reverts
 * those edits to the LLM's original IR after the rehydration fetch —
 * silently, with no warning.
 *
 * This helper persists the edited IR into sessionStorage keyed by
 * `draft_id`, scoped to the current tab so cross-tab confusion is
 * minimal. The data lives only as long as the tab and gets cleared when
 * the wizard commits or the user starts over.
 */
import { useCallback } from "react";
import type { MonitorIR } from "@/monitors/schemas";

export const MONITORS_WIZARD_IR_KEY_PREFIX = "monitors:wizard:ir:";
const KEY_PREFIX = MONITORS_WIZARD_IR_KEY_PREFIX;

function key(draftId: string): string {
  return `${KEY_PREFIX}${draftId}`;
}

export function readStashedIR(draftId: string): MonitorIR | null {
  try {
    const raw = sessionStorage.getItem(key(draftId));
    if (!raw) return null;
    return JSON.parse(raw) as MonitorIR;
  } catch {
    // Corrupt entry, quota error, sessionStorage disabled (Safari private
    // mode) — fall through to "no stash". Caller treats this as "edits
    // weren't preserved" and uses the hydrated IR from the backend.
    return null;
  }
}

export function writeStashedIR(draftId: string, ir: MonitorIR): void {
  try {
    sessionStorage.setItem(key(draftId), JSON.stringify(ir));
  } catch {
    // Quota or storage disabled — silently swallow. The edits don't
    // survive reload but the wizard continues to function. We choose this
    // over throwing because the IR-editing UX should never feel fragile.
  }
}

export function clearStashedIR(draftId: string): void {
  try {
    sessionStorage.removeItem(key(draftId));
  } catch {
    // Ignored — same rationale as writeStashedIR.
  }
}

/**
 * Returns a callback that the wizard's Step 2 IRForm `onChange` handler
 * can invoke to stash the edited IR. Memoized so the IRForm doesn't see
 * a new function identity on every render.
 */
export function useIRStasher(
  draftId: string | null,
): (ir: MonitorIR) => void {
  return useCallback(
    (ir: MonitorIR) => {
      if (draftId) writeStashedIR(draftId, ir);
    },
    [draftId],
  );
}
